import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { apiFetch } from '@/lib/api-fetch';
import { getVaultBlob, getVaultMetadata, setServerLastModified, setVaultSyncEnabled, setVaultSyncId } from '../../utils/vault-storage';
import type { VaultRegisterResponse } from '@syfo/types/api.client.types';
import { syncMachineService } from '../../services/sync-state';

/**
 * Register a new vault on the server
 * This is required before sync can be enabled for a local vault
 * The server assigns a syncId (server vaultId) which is stored separately from the client vaultId
 * Returns the server-assigned syncId and lastModified
 */
const registerOnServer = async (
  encryptedData: string
): Promise<{ syncId: string; lastModified: string }> => {
  // Make API call - POST with base64-encoded encrypted data as body
  const response = await apiFetch('/api/vaults', {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain'
    },
    body: encryptedData // Already base64 encoded in localStorage
  });

  // Handle errors
  if (!response.ok) {
    throw new Error(`Failed to register vault: ${response.status} ${response.statusText}`);
  }

  // Parse response - server returns vaultId which we store as syncId
  const result: VaultRegisterResponse = await response.json();
  return { syncId: result.vaultId, lastModified: result.lastModified };
};

/**
 * Thunk to register a local vault with the server
 * This enables sync by:
 * 1. Uploading the vault data to the server
 * 2. Receiving a server-assigned syncId (stored separately from client vaultId)
 * 3. Storing the syncId and lastModified in localStorage
 * 4. Enabling sync for the vault
 * 
 * The client vaultId remains unchanged - syncId is used for all server API calls
 */
export const registerVaultThunk = createAsyncThunk<
  { vaultId: string; syncId: string; lastModified: string },
  { vaultId: string },
  { state: RootState }
>(
  'vault/register',
  async ({ vaultId }) => {
    // Get encrypted data from localStorage
    const encryptedData = getVaultBlob(vaultId);
    
    if (!encryptedData) {
      throw new Error('No vault data found to register');
    }

    // Get vault metadata
    const metadata = getVaultMetadata(vaultId);
    if (!metadata) {
      throw new Error('No vault metadata found to register');
    }

    // Transition machine: local-only → registering
    const machine = syncMachineService.getOrCreate(vaultId);
    machine.register();

    try {
      // Register vault on server - returns syncId (server's vaultId)
      const { syncId, lastModified } = await registerOnServer(encryptedData);
      
      // Store the syncId (server-assigned ID for API calls)
      setVaultSyncId(vaultId, syncId);
      
      // Store the server's Last-Modified date
      setServerLastModified(vaultId, lastModified);
      
      // Enable sync for the vault
      setVaultSyncEnabled(vaultId, true);

      // Transition machine: registering → idle
      machine.registered({ serverId: syncId, serverLastModified: lastModified, lastSyncDate: lastModified });
      
      return { vaultId, syncId, lastModified };
    } catch (error) {
      // Transition machine to error state so the UI can reflect the failure
      machine.fail('register', error instanceof Error ? error.message : String(error));
      throw error;
    }
  }
);
